import React, { useState } from 'react';
import styled from 'styled-components';
import RadioButton from 'shared/components/molecules/RadioButton';

const Container = styled.div`
  margin-top: 1.5em;
`;

const TiersRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1em;
  margin-top: 0.5em;

  .box-tier-1 {
    border: 2px solid rgb(207, 121, 56);
    border-radius: 4px;
    padding: 0.5em 0;
    background-color: rgba(207, 121, 56, 0.1);
  }
`;

type DDOSProtectionProps = {
  options?: string[] | null;
  selected?: string | null;
  readonly: boolean;
  onChange?: (tier: string | null) => void;
};

const TIER_TOOLTIPS: Record<string, string> = {
  'None': 
    'Your DIA service will have no DDoS protection.',
  'Tier 0':
    'Benefit from our DDoS protected core network, included free of charge on all DIA services',
  'Tier 1':
    '<b>Recommended:</b> 24/7 DDoS monitoring, with monthly reports and fast upgrade capability to DDoS Mitigation.',
  'Tier 2':
    'All the features of DDoS monitoring with the addition of always on auto-mitigation.',
  'Tier 3':
    'Premium DDoS protection with dedicated resources and custom mitigation strategies.\n\nReal-time alerts, full incident management, and tailored reporting.\n\nDesigned for organisations with the highest security requirements.',
};

export const TIER_NAMES: Record<string, string> = {
  'None': 'No',
  'Tier 0': 'Standard',
  'Tier 1': 'Monitoring',
  'Tier 2': 'Mitigation',
  'Tier 3': 'Advanced Mitigation',
};

const DDOSProtection: React.FC<DDOSProtectionProps> = ({ options, selected, readonly, onChange }) => {
  // If no options are provided, display nothing
  if (!options || options.length === 0) return null;

  // Filter for allowed tiers
  const availableTiers = options.filter(
    (opt): opt is 'None' |'Tier 0' | 'Tier 1' | 'Tier 2' | 'Tier 3' =>
      ['Tier 1', 'Tier 2'].includes(opt)
  );

  if (availableTiers.length === 0) return null;

  // Add 'None' option 
  availableTiers.unshift('None'); 

  const [tier, setTier] = useState<string | null>(selected ?? null);

  // Update local state if selected prop changes
  React.useEffect(() => {
    setTier(selected ?? null);
  }, [selected]);

  // Notify parent when tier changes
  React.useEffect(() => {
    if (onChange) {
      onChange(tier === 'None' ? null : (tier ?? null));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tier]);

  return (
    <Container>
      <div className="mt-4 mb-2 addon_options__title">Do you require DDoS Protection?</div>
      <p className='text-muted mb-4'>DIA service level DDoS protection. Please choose a Tier:</p>
      <TiersRow>
        {availableTiers.map(opt => (
          <div key={opt} className={`row box-${opt.replace(' ', '-').toLowerCase()}`}>
            <div className={`col-12 col-md-4`}>
              <RadioButton
                key={opt}
                description={TIER_NAMES[opt]}
                selected={tier === opt || (opt === 'None' && !tier)}
                onClick={() => setTier(opt)}
                id={`tier-${opt}`}
                size="fill"
                disabled={readonly}
                // tooltipText={TIER_TOOLTIPS[opt]}
              />
            </div>
            <div className='col-12 col-md-8' style={{ fontSize: '0.9em' }}>
              <div dangerouslySetInnerHTML={{ __html: TIER_TOOLTIPS[opt] }} />
            </div>
          </div>
        ))}
      </TiersRow>
    </Container>
  );
};

export default DDOSProtection;